import { useState, useId, type FormEvent } from "react";
import { ClipboardCopy, Loader2, AlertCircle } from "lucide-react";
import { useAuth } from "./AuthProvider";

export function SignInForm() {
  const { signIn, completeNewPassword } = useAuth();
  const [stage, setStage] = useState<"signin" | "new_password">("signin");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const emailId = useId();
  const passwordId = useId();
  const newPasswordId = useId();
  const confirmPasswordId = useId();

  async function handleSignIn(e: FormEvent) {
    e.preventDefault();
    setError("");

    if (!email.trim() || !password) {
      setError("请输入邮箱和密码");
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await signIn(email.trim(), password);
      if (result.newPasswordRequired) {
        setStage("new_password");
      }
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "登录失败，请检查邮箱和密码";
      setError(translateCognitoError(message));
    } finally {
      setIsSubmitting(false);
    }
  }

  async function handleNewPassword(e: FormEvent) {
    e.preventDefault();
    setError("");

    if (newPassword.length < 8) {
      setError("密码长度至少 8 位");
      return;
    }
    if (newPassword !== confirmPassword) {
      setError("两次输入的密码不一致");
      return;
    }

    setIsSubmitting(true);
    try {
      await completeNewPassword(newPassword);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "设置密码失败";
      setError(translateCognitoError(message));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <div className="w-full max-w-md bg-surface rounded-2xl shadow-xl p-8">
        <div className="flex items-center justify-center mb-6">
          <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10">
            <ClipboardCopy className="h-7 w-7 text-primary" aria-hidden="true" />
          </div>
        </div>
        <h1 className="text-2xl font-bold text-foreground mb-2 text-center">
          管理后台
        </h1>
        <p className="text-muted text-sm mb-8 text-center">
          {stage === "signin" ? "请使用管理员账号登录" : "首次登录请设置新密码"}
        </p>

        {error && (
          <div
            role="alert"
            className="mb-5 flex items-start gap-2 rounded-lg bg-error-light px-4 py-3"
          >
            <AlertCircle className="h-5 w-5 text-error shrink-0 mt-0.5" aria-hidden="true" />
            <p className="text-sm text-error font-medium">{error}</p>
          </div>
        )}

        {stage === "signin" ? (
          <form onSubmit={handleSignIn} className="space-y-4">
            <div>
              <label
                htmlFor={emailId}
                className="block text-sm font-medium text-foreground mb-1.5"
              >
                邮箱
              </label>
              <input
                id={emailId}
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@example.com"
                disabled={isSubmitting}
                className="w-full rounded-lg border border-border px-4 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary disabled:opacity-50"
              />
            </div>
            <div>
              <label
                htmlFor={passwordId}
                className="block text-sm font-medium text-foreground mb-1.5"
              >
                密码
              </label>
              <input
                id={passwordId}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isSubmitting}
                className="w-full rounded-lg border border-border px-4 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary disabled:opacity-50"
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 text-base font-medium text-white shadow-sm transition-colors duration-200 hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                  登录中…
                </>
              ) : (
                "登录"
              )}
            </button>
          </form>
        ) : (
          <form onSubmit={handleNewPassword} className="space-y-4">
            <div>
              <label
                htmlFor={newPasswordId}
                className="block text-sm font-medium text-foreground mb-1.5"
              >
                新密码
              </label>
              <input
                id={newPasswordId}
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={isSubmitting}
                className="w-full rounded-lg border border-border px-4 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary disabled:opacity-50"
              />
              <p className="mt-1.5 text-xs text-muted">
                至少 8 位，需包含大小写字母和数字
              </p>
            </div>
            <div>
              <label
                htmlFor={confirmPasswordId}
                className="block text-sm font-medium text-foreground mb-1.5"
              >
                确认新密码
              </label>
              <input
                id={confirmPasswordId}
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={isSubmitting}
                className="w-full rounded-lg border border-border px-4 py-2.5 text-base focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary disabled:opacity-50"
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full inline-flex cursor-pointer items-center justify-center gap-2 rounded-lg bg-primary px-6 py-3 text-base font-medium text-white shadow-sm transition-colors duration-200 hover:bg-primary-dark disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" />
                  设置中…
                </>
              ) : (
                "设置密码并登录"
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── Error Translation ──────────────────────────────────────────────────────

function translateCognitoError(message: string): string {
  if (/NotAuthorizedException|Incorrect username or password/i.test(message)) {
    return "邮箱或密码错误";
  }
  if (/UserNotFoundException/i.test(message)) {
    return "此邮箱未注册管理员账号";
  }
  if (/InvalidPasswordException|Password not long enough|Password must/i.test(message)) {
    return "密码不符合要求（至少 8 位，需包含大小写字母和数字）";
  }
  if (/PasswordResetRequired/i.test(message)) {
    return "需要重置密码，请联系管理员";
  }
  if (/UserNotConfirmedException/i.test(message)) {
    return "账号未确认";
  }
  if (/Network|Failed to fetch|TypeError/i.test(message)) {
    return "网络连接失败，请检查网络后重试";
  }
  return message;
}
